import React, { useState, useEffect } from 'react';
import { Utils, authJsonHeaders, formatDate, handleSessionExpired } from '../../utils';
import { MdSearch } from 'react-icons/md';
import ListPaginationBar from '../common/ListPaginationBar';

const AdminApplications = () => {
    const [apps, setApps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });

    useEffect(() => {
        fetchApps();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination.page, search]);

    const fetchApps = async () => {
        setLoading(true);
        try {
            const response = await fetch(Utils.baseurl + 'admin/applications', {
                method: 'POST',
                headers: authJsonHeaders(Utils.storageKeys.adminToken),
                body: JSON.stringify({ page: pagination.page, limit: pagination.limit, search })
            });
            const result = await response.json();
            if (handleSessionExpired(response, result)) return;
            if (result.status) {
                setApps(result.data);
                setPagination(prev => ({ ...prev, ...result.pagination }));
            }
        } catch (error) {
            console.error('Error fetching apps:', error);
        }
        setLoading(false);
    };

    const getChannelType = (type) => {
        switch (type) {
            case 1: return { label: 'iOS', color: '#000' };
            case 2: return { label: 'Android', color: '#3ddc84' };
            case 3: return { label: 'Web', color: '#4f46e5' };
            default: return { label: 'Unknown', color: '#9ca3af' };
        }
    };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <div>
                    <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#1a1a1a', margin: 0 }}>Applications</h1>
                    <p style={{ color: '#6b7280', marginTop: '8px' }}>All registered applications</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: 'white', padding: '8px 16px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                    <MdSearch size={20} color="#9ca3af" />
                    <input
                        type="text"
                        placeholder="Search apps..."
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}
                        style={{ border: 'none', outline: 'none', fontSize: '14px', width: '200px' }}
                    />
                </div>
            </div>

            <div style={{ background: 'white', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ background: '#f9fafb' }}>
                            <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '14px' }}>App Name</th>
                            <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '14px' }}>App ID</th>
                            <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '14px' }}>Owner</th>
                            <th style={{ padding: '16px', textAlign: 'center', fontWeight: '600', color: '#374151', fontSize: '14px' }}>Platform</th>
                            <th style={{ padding: '16px', textAlign: 'center', fontWeight: '600', color: '#374151', fontSize: '14px' }}>Users</th>
                            <th style={{ padding: '16px', textAlign: 'center', fontWeight: '600', color: '#374151', fontSize: '14px' }}>Subscriptions</th>
                            <th style={{ padding: '16px', textAlign: 'center', fontWeight: '600', color: '#374151', fontSize: '14px' }}>Created</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading...</td></tr>
                        ) : apps.length === 0 ? (
                            <tr><td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>No applications found</td></tr>
                        ) : apps.map(app => {
                            const channel = getChannelType(app.channel_type);
                            return (
                                <tr key={app.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                    <td style={{ padding: '16px' }}>
                                        <div style={{ fontWeight: '500', color: '#1a1a1a' }}>{app.application_name}</div>
                                        <div style={{ fontSize: '12px', color: '#9ca3af' }}>{app.organization?.oganization_name || 'No Org'}</div>
                                    </td>
                                    <td style={{ padding: '16px', color: '#6b7280', fontSize: '12px', fontFamily: 'monospace' }}>{app.app_id}</td>
                                    <td style={{ padding: '16px', color: '#374151' }}>{app.user?.email || 'N/A'}</td>
                                    <td style={{ padding: '16px', textAlign: 'center' }}>
                                        <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '500', background: `${channel.color}20`, color: channel.color }}>
                                            {channel.label}
                                        </span>
                                    </td>
                                    <td style={{ padding: '16px', textAlign: 'center', color: '#374151', fontWeight: '500' }}>{app.app_users_count || 0}</td>
                                    <td style={{ padding: '16px', textAlign: 'center', color: '#374151', fontWeight: '500' }}>{app.subscriptions_count || 0}</td>
                                    <td style={{ padding: '16px', textAlign: 'center', color: '#6b7280', fontSize: '14px' }}>
                                        {formatDate(app.createdAt)}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>

                <ListPaginationBar
                    page={pagination.page}
                    setPage={(n) => setPagination((p) => ({ ...p, page: n }))}
                    total={pagination.total}
                    pageSize={pagination.limit}
                    totalPages={pagination.totalPages}
                    ariaLabel="Admin applications pagination"
                    className="px-3 py-3"
                    style={{ borderTop: '1px solid #f3f4f6' }}
                    loading={loading}
                />
            </div>
        </div>
    );
};

export default AdminApplications;
